import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, BarChart3, Info, Menu, Newspaper, Play, RefreshCw, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import BrandLogo from "../components/BrandLogo";
import BannerCarousel from "../components/BannerCarousel";
import TickerMarquee from "../components/TickerMarquee";
import { getMarketWindowForNow } from "../utils/marketStatus";
import { isAppWebView } from "../utils/appWebView";
import { openExternalLink } from "../utils/appInteractions";

function Home() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [bannersLoaded, setBannersLoaded] = useState(false);
  const [settings, setSettings] = useState({});
  const [markets, setMarkets] = useState([]);
  const [activeBanner, setActiveBanner] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [liveTime, setLiveTime] = useState(() => formatLiveTime(new Date()));
  const [stickyVisible, setStickyVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [normalRateMap, setNormalRateMap] = useState({});

  const isDesktop = viewportWidth >= 1024;
  const isLaptop = viewportWidth >= 1024 && viewportWidth <= 1366;
  const inAppWebView = isAppWebView();
  const displayBanners = useMemo(() => (banners.length ? banners : []), [banners]);
  const displayGameRates = useMemo(() => buildHomeRateCards(normalRateMap), [normalRateMap]);
  const downloadLabel = useMemo(() => {
    const label = String(settings.app_download_label || "").trim();
    return label || "Download App Now";
  }, [settings.app_download_label]);
  const displayMarkets = useMemo(
    () =>
      markets.length
        ? markets.slice(0, 12).map((item) => ({
            id: item.id,
            name: item.name,
            result: item.result || "***-**-***",
            time: (() => {
              const marketWindow = getMarketWindowForNow(item);
              return `${formatTime(marketWindow.open_time || item.open_time)} - ${formatTime(marketWindow.close_time || item.close_time)}`;
            })(),
          }))
        : fallbackMarkets,
    [markets]
  );

  const loadBanners = useCallback(async () => {
    try {
      const res = await API.get("/banners");
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBanners([]);
    } finally {
      setBannersLoaded(true);
    }
  }, []);

  const loadMarkets = useCallback(async () => {
    try {
      const res = await API.get("/markets");
      const data = Array.isArray(res.data)
        ? res.data
            .filter((item) => (item.market_type || "normal") !== "starline")
            .sort(sortMarketsByOpenTime)
        : [];
      setMarkets(data);
    } catch {
      setMarkets([]);
    }
  }, []);

  const loadHomeData = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadBanners(),
        loadMarkets(),
        API.get("/game-rates?market_type=normal").then((res) => setNormalRateMap(res.data || {})).catch(() => setNormalRateMap({})),
        API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {}),
      ]);
      setLiveTime(formatLiveTime(new Date()));
    } finally {
      setRefreshing(false);
    }
  }, [loadBanners, loadMarkets]);

  useEffect(() => {
    loadHomeData();
  }, [loadHomeData]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadHomeData();
    }, 30000);
    return () => window.clearInterval(interval);
  }, [loadHomeData]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    const onScroll = () => setStickyVisible(window.scrollY > 220);
    const onTick = () => setLiveTime(formatLiveTime(new Date()));

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    const clock = window.setInterval(onTick, 1000);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      window.clearInterval(clock);
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveBanner((current) => (current + 1) % Math.max(displayBanners.length, 1));
    }, 3500);
    return () => window.clearInterval(timer);
  }, [displayBanners.length]);

  useEffect(() => {
    if (!isDesktop) return undefined;
    if (!menuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isDesktop, menuOpen]);

  const openDashboard = () => navigate("/dashboard");
  const openCharts = () => navigate("/charts");
  const openMarketChart = (marketId, view = "jodi") => {
    if (marketId) navigate(`/charts/market/${marketId}?view=${view}`);
    else navigate("/charts");
  };
  const openLegal = () => navigate("/dashboard");
  const openDownload = () => {
    const url = String(settings.app_download_url || "").trim();
    if (url) {
      openExternalLink(url);
      return;
    }
    alert("Download link will be added later");
  };

  return (
    <main style={page}>
      {menuOpen && <div style={overlay} onClick={() => setMenuOpen(false)} />}

      {isDesktop ? (
        <>
          <aside style={{ ...desktopSidebar, transform: menuOpen ? "translateX(0)" : "translateX(-110%)" }}>
            <SidebarContent closeMenu={() => setMenuOpen(false)} openDashboard={openDashboard} openCharts={openCharts} openDownload={openDownload} openLegal={openLegal} />
          </aside>

          <div style={{ ...desktopMainShell, ...(isLaptop ? desktopMainShellLaptop : null) }}>
            <header style={{ ...desktopTopbar, ...(isLaptop ? desktopTopbarLaptop : null) }}>
              <button type="button" style={brandButton} onClick={openDashboard}>
                <BrandLogo size="sm" />
              </button>

              <nav style={{ ...desktopNav, ...(isLaptop ? desktopNavLaptop : null) }}>
                <TopNavButton label="Home" active laptop={isLaptop} onClick={openDashboard} />
                <TopNavButton label="MK Charts" laptop={isLaptop} onClick={openCharts} />
                <TopNavButton label="MK Blog" laptop={isLaptop} onClick={openDashboard} />
                <TopNavButton label="MK About" laptop={isLaptop} onClick={openDashboard} />
              </nav>

              <div style={{ ...desktopActions, ...(isLaptop ? desktopActionsLaptop : null) }}>
                <button type="button" style={{ ...desktopPlayButton, ...(isLaptop ? desktopActionButtonLaptop : null) }} onClick={openDashboard}>
                  <Play size={16} /> Play Online
                </button>
                <button type="button" style={{ ...desktopDownloadButton, ...(isLaptop ? desktopActionButtonLaptop : null) }} onClick={openDownload}>
                  <ArrowRight size={16} /> Download
                </button>
                <button type="button" style={{ ...desktopMenuButton, ...(isLaptop ? desktopMenuButtonLaptop : null) }} onClick={() => setMenuOpen(true)}>
                  <Menu size={24} />
                </button>
              </div>
            </header>

            <section style={{ ...desktopContent, ...(isLaptop ? desktopContentLaptop : null) }}>
              <HeroSection
                activeBanner={activeBanner}
                displayBanners={displayBanners}
                bannersLoaded={bannersLoaded}
                openDashboard={openDashboard}
                openDownload={openDownload}
                downloadLabel={downloadLabel}
                runningNotice={settings.home_running_notice}
                desktop
                laptop={isLaptop}
              />
              <HomeSections gameRates={displayGameRates} displayMarkets={displayMarkets} liveTime={liveTime} openDashboard={openDashboard} openCharts={openCharts} openMarketChart={openMarketChart} openDownload={openDownload} openLegal={openLegal} refreshHome={loadHomeData} refreshing={refreshing} settings={settings} showDownload={!inAppWebView} desktop laptop={isLaptop} downloadLabel={downloadLabel} />
            </section>
          </div>
        </>
      ) : (
        <>
          <aside style={{ ...mobileDrawer, transform: menuOpen ? "translateX(0)" : "translateX(-100%)" }}>
            <SidebarContent closeMenu={() => setMenuOpen(false)} openDashboard={openDashboard} openCharts={openCharts} openDownload={openDownload} openLegal={openLegal} />
          </aside>

          <header style={mobileTopbar}>
            <button type="button" style={brandButton} onClick={openDashboard}>
              <BrandLogo size="sm" />
            </button>
            <button type="button" style={mobileMenuButton} onClick={() => setMenuOpen(true)}>
              <Menu size={28} />
            </button>
          </header>

          <HeroSection
            activeBanner={activeBanner}
            displayBanners={displayBanners}
            bannersLoaded={bannersLoaded}
            openDashboard={openDashboard}
            openDownload={openDownload}
            downloadLabel={downloadLabel}
            runningNotice={settings.home_running_notice}
          />
          <HomeSections gameRates={displayGameRates} displayMarkets={displayMarkets} liveTime={liveTime} openDashboard={openDashboard} openCharts={openCharts} openMarketChart={openMarketChart} openDownload={openDownload} openLegal={openLegal} refreshHome={loadHomeData} refreshing={refreshing} settings={settings} showDownload={!inAppWebView} downloadLabel={downloadLabel} />
        </>
      )}

      {stickyVisible && (
        <button type="button" style={stickyDownloadButton} onClick={openDownload}>
          <span style={downloadCenter}>
            <span style={downloadIcon}>
              <img src="/Google_Play_2016_icon.svg" alt="" style={downloadIconImg} />
            </span>
            <strong style={downloadSingleText}>{downloadLabel}</strong>
          </span>
        </button>
      )}
    </main>
  );
}

function HeroSection({ activeBanner, displayBanners, bannersLoaded, openDashboard, openDownload, downloadLabel, runningNotice = "", desktop = false, laptop = false }) {
  const tickerText = String(runningNotice || "Auto Deposit | Auto Withdraw | Fast Results | Secure Wallet | Official Matka Kings Updates").trim();
  return (
    <section style={{ ...heroWrap, ...(desktop ? heroWrapDesktop : null) }}>
      <button type="button" style={{ ...heroCard, ...(desktop ? heroCardDesktop : null), ...(laptop ? heroCardLaptop : null) }} onClick={openDashboard}>
        <TickerMarquee
          text={tickerText}
          speed={58}
          outerStyle={homeTickerWrap}
          trackStyle={homeTickerTrack}
          textStyle={homeTickerText}
        />
        <BannerCarousel
          banners={displayBanners}
          activeIndex={activeBanner}
          desktop={desktop}
          laptop={laptop}
          imageStyle={heroImage}
          desktopImageStyle={heroImageDesktop}
          laptopImageStyle={heroImageLaptop}
          altPrefix="Matka Kings banner"
          fallback={
            <div style={{ ...heroFallback, ...(desktop ? heroFallbackDesktop : null), ...(laptop ? heroFallbackLaptop : null) }}>
              {bannersLoaded ? (
                <>
                  <BrandLogo light size={desktop ? "sm" : "xs"} />
                  <p style={heroText}>India's biggest matka platform</p>
                </>
              ) : null}
            </div>
          }
        />
      </button>

      <button type="button" style={downloadStrip} onClick={openDownload}>
        <span style={downloadCenter}>
          <span style={downloadIcon}>
            <img src="/Google_Play_2016_icon.svg" alt="" style={downloadIconImg} />
          </span>
          <strong style={downloadSingleText}>{downloadLabel}</strong>
        </span>
      </button>
    </section>
  );
}

function HomeSections({ gameRates, displayMarkets, liveTime, openDashboard, openCharts, openMarketChart, openDownload, openLegal, refreshHome, refreshing = false, settings = {}, showDownload = true, desktop = false, laptop = false, downloadLabel }) {
  return (
    <section id="game-rates-section" style={{ ...content, ...(desktop ? contentDesktop : null), ...(laptop ? contentLaptop : null) }}>
      <div style={sectionTitleWrap}>
        <span style={sectionAccent} />
        <div>
          <h2 style={sectionTitle}>Game Rates</h2>
          <p style={sectionSubtitle}>Best payouts in the market</p>
        </div>
      </div>

      <div style={{ ...rateList, ...(desktop ? rateListDesktop : null), ...(laptop ? rateListLaptop : null) }}>
        {gameRates.map((item) => (
          <button
            key={item.title}
            type="button"
            style={{
              ...rateCard,
              ...(desktop ? rateCardDesktop : null),
              ...(laptop ? rateCardLaptop : null),
              ...(desktop && item.title === "Full Sangam" ? fullWidthRateCard : null),
              borderColor: item.border,
              background: item.bg,
            }}
            onClick={openDashboard}
          >
            <span style={{ ...rateIcon, background: item.iconBg, color: item.color }}>{item.icon}</span>
            <div style={rateBody}>
              <strong style={rateTitle}>{item.title}</strong>
              <small style={rateDesc}>{item.desc}</small>
            </div>
            <div style={{ ...rateValue, color: item.color }}>
              <strong style={rateAmount}>{formatKaRate(item.rateValue)}</strong>
              <small style={rateUnit}>rate</small>
            </div>
          </button>
        ))}
      </div>

      <div style={liveHeaderRow}>
        <div style={sectionTitleWrap}>
          <span style={sectionAccent} />
          <div>
            <h2 style={sectionTitle}>Live Results</h2>
            <p style={sectionSubtitle}>Updated {liveTime}</p>
          </div>
        </div>

        <button type="button" style={refreshButton} onClick={refreshHome} disabled={refreshing}>
          <RefreshCw size={16} style={refreshing ? spinIcon : null} /> {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div style={{ ...marketList, ...(desktop ? marketListDesktop : null), ...(laptop ? marketListLaptop : null) }}>
        {displayMarkets.map((market) => (
          <div
            key={market.id || market.name}
            role="button"
            tabIndex={0}
            style={{ ...marketCard, ...(desktop ? marketCardDesktop : null), ...(laptop ? marketCardLaptop : null) }}
            onClick={openDashboard}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") openDashboard();
            }}
          >
            <span aria-hidden="true" style={marketGlossSweep} />
            <div style={marketBody}>
              <strong style={marketName}>{market.name}</strong>
              <div style={marketResult}>{market.result}</div>
              <span style={marketLinks}>
                <button
                  type="button"
                  style={marketChartLink}
                  onClick={(event) => {
                    event.stopPropagation();
                    openMarketChart ? openMarketChart(market.id, "jodi") : openCharts();
                  }}
                >
                  Jodi Chart
                </button>
                <span style={marketLinkDivider}>|</span>
                <button
                  type="button"
                  style={marketChartLink}
                  onClick={(event) => {
                    event.stopPropagation();
                    openMarketChart ? openMarketChart(market.id, "panel") : openCharts();
                  }}
                >
                  Panel Chart
                </button>
              </span>
            </div>
            <span style={playSquare}>
              <Play size={18} fill="#fff" color="#fff" />
            </span>
          </div>
        ))}
      </div>

      <div style={sectionTitleWrap}>
        <span style={sectionAccent} />
        <div>
          <h2 style={sectionTitle}>More Games</h2>
          <p style={sectionSubtitle}>Explore our exciting game collection</p>
        </div>
      </div>

      <div style={{ ...moreGamesList, ...(desktop ? moreGamesListDesktop : null) }}>
        {moreGames.map((item) => (
          <button
            key={item.title}
            type="button"
            style={{ ...moreGameCard, background: item.background }}
            onClick={openDashboard}
          >
            <span style={moreGameIconWrap}>{item.icon}</span>
            <div style={moreGameBody}>
              <strong style={moreGameTitle}>{item.title}</strong>
              <small style={moreGameSubtitle}>{item.subtitle}</small>
            </div>
            <span style={moreGameArrow}>›</span>
          </button>
        ))}
      </div>

      {showDownload && String(settings.app_download_url || "").trim() ? (
        <button type="button" style={colorDownloadButton} onClick={openDownload}>
          <span style={downloadCenter}>
            <span style={colorDownloadIconWrap}>
              <img src="/Google_Play_2016_icon.svg" alt="" style={downloadIconImg} />
            </span>
            <strong style={downloadSingleText}>{downloadLabel}</strong>
          </span>
        </button>
      ) : null}

      <button type="button" style={playOnlineButton} onClick={openDashboard}>
        <Play size={18} fill="#fff" color="#fff" />
        <span>Play Online</span>
        <ArrowRight size={18} />
      </button>

      <section style={seoCard}>
        <div style={sectionTitleWrap}>
          <span style={sectionAccent} />
          <div>
            <h2 style={sectionTitle}>Popular Matka Searches</h2>
            <p style={sectionSubtitle}>Quick links for online matka play, Aviator and Color Game</p>
          </div>
        </div>
        <div style={keywordGrid}>
          {seoKeywords.map((item) => (
            <button key={item.label} type="button" style={keywordChip} onClick={openDashboard}>
              <span>{item.label}</span>
              <small>{item.note}</small>
            </button>
          ))}
        </div>
      </section>

      <section style={licenseCard}>
        <h3 style={licenseTitle}>Our Gaming Licenses</h3>
        <p style={licenseSubtitle}>Certified & Regulated Gaming Platform</p>
        <div style={licenseLogoRow}>
          <span style={licenseLogo}>NGA</span>
          <span style={licenseLogo}>Orbit Gaming</span>
          <span style={licenseLogo}>SafeBetic</span>
        </div>
      </section>

      <footer style={footerCard}>
        <BrandLogo light size="sm" />
        <p style={footerLead}>Players need to be 18+ in order to register. Underage gaming is prohibited.</p>
        <p style={footerAddress}>Rosebank 18, Harbor City, Malta</p>
        <p style={footerText}>
          This platform is managed by <strong>Blue Orbit Interactive Ltd.</strong>, a regulated digital entertainment company.
          Licensed software and security services are supported by <strong>Northbay Systems B.V.</strong> and <strong>SafeBetic Compliance Group</strong>.
        </p>
        <div style={legalLinks}>
          {legalPages.map((item) => (
            <button key={item.path} type="button" style={legalLink} onClick={() => openLegal(item.path)}>
              {item.label}
            </button>
          ))}
        </div>
        <div style={footerDivider} />
        <p style={footerCopyright}>© 2026 Matka Kings. All Rights Reserved.</p>
      </footer>
    </section>
  );
}

function SidebarContent({ closeMenu, openDashboard, openCharts, openDownload, openLegal }) {
  const run = (fn) => () => {
    if (closeMenu) closeMenu();
    fn();
  };

  return (
    <>
      <div style={drawerTop}>
            <button type="button" style={brandButton} onClick={run(openDashboard)}>
              <BrandLogo size="sm" />
            </button>
        <button type="button" style={drawerClose} onClick={closeMenu}>
          <X size={18} />
        </button>
      </div>

      <div style={welcomeCard}>
        <div>
          <strong style={welcomeTitle}>Welcome !</strong>
          <div style={welcomeSub}>Login to start playing</div>
        </div>
        <button type="button" style={welcomeButton} onClick={run(openDashboard)}>
          <Play size={16} /> Play Online
        </button>
      </div>

      <nav style={drawerNav}>
        <DrawerItem icon={<BarChart3 size={18} />} label="Home" active onClick={run(openDashboard)} />
        <DrawerItem icon={<BarChart3 size={18} />} label="MK Charts" onClick={run(openCharts)} />
        <DrawerItem icon={<Newspaper size={18} />} label="MK Blog" onClick={run(openDashboard)} />
        <DrawerItem icon={<Info size={18} />} label="MK About" onClick={run(openDashboard)} />
        <DrawerItem icon={<Info size={18} />} label="Privacy Policy" onClick={run(openDashboard)} />
        <DrawerItem icon={<Info size={18} />} label="Terms" onClick={run(openDashboard)} />
        <DrawerItem icon={<Info size={18} />} label="Contact Us" onClick={run(openDashboard)} />
        <DrawerItem icon={<ArrowRight size={18} />} label="Download App" highlight onClick={run(openDownload)} />
      </nav>
    </>
  );
}

function DrawerItem({ icon, label, active = false, highlight = false, onClick }) {
  return (
    <button
      type="button"
      style={{
        ...drawerItem,
        ...(active ? drawerItemActive : null),
        ...(highlight ? drawerItemHighlight : null),
      }}
      onClick={onClick}
    >
      <span style={{ ...drawerIcon, ...(active ? drawerIconActive : null), ...(highlight ? drawerIconHighlight : null) }}>{icon}</span>
      <strong style={drawerLabel}>{label}</strong>
      <ArrowRight size={16} color={highlight ? "#fff" : active ? "#fff" : "#94a3b8"} />
    </button>
  );
}

function TopNavButton({ label, active = false, laptop = false, onClick }) {
  return (
    <button type="button" style={{ ...(active ? topNavButtonActive : topNavButton), ...(laptop ? topNavButtonLaptop : null) }} onClick={onClick}>
      {label}
    </button>
  );
}

function formatTime(value) {
  if (!value) return "--:--";
  const [hourRaw, minute = "00"] = String(value).split(":");
  const hour = Number(hourRaw);
  const suffix = hour >= 12 ? "pm" : "am";
  const displayHour = ((hour + 11) % 12) + 1;
  return `${String(displayHour).padStart(2, "0")}:${minute} ${suffix}`;
}

function formatLiveTime(date) {
  return date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

const sortMarketsByOpenTime = (a, b) => {
  const timeDiff = toMarketMinutes(a.open_time) - toMarketMinutes(b.open_time);
  if (timeDiff !== 0) return timeDiff;

  const numberA = Number(String(a.market_number || a.number || a.id || a.name || "").match(/\d+/)?.[0] || 0);
  const numberB = Number(String(b.market_number || b.number || b.id || b.name || "").match(/\d+/)?.[0] || 0);
  if (numberA !== numberB) return numberA - numberB;

  return String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true });
};

const toMarketMinutes = (value) => {
  const match = String(value || "").match(/(\d{1,2}):(\d{2})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return Number(match[1]) * 60 + Number(match[2]);
};

const fallbackMarkets = [
  { id: 1, name: "MILAN MORNING", result: "127-03-157", time: "10:10 am - 11:11 am" },
  { id: 2, name: "SRIDEVI", result: "689-38-468", time: "11:25 am - 12:25 pm" },
  { id: 3, name: "TIME BAZAR", result: "569-00-136", time: "12:45 pm - 01:45 pm" },
  { id: 4, name: "KANTARA", result: "678-18-567", time: "03:25 pm - 05:25 pm" }
];

const HOME_RATE_META = [
  { key: "single", title: "Single Digit", desc: "Guess 1 Number", icon: "1", color: "#2563eb", border: "#bfdbfe", bg: "linear-gradient(135deg,#ffffff,#eff6ff)", iconBg: "#eff6ff" },
  { key: "jodi", title: "Jodi Digit", desc: "Guess 2 Numbers", icon: "J2", color: "#7c3aed", border: "#e9d5ff", bg: "linear-gradient(135deg,#ffffff,#f5f3ff)", iconBg: "#f5f3ff" },
  { key: "sp", title: "Single Pana", desc: "3 Unique Digits", icon: "SP", color: "#16a34a", border: "#bbf7d0", bg: "linear-gradient(135deg,#ffffff,#ecfdf5)", iconBg: "#ecfdf5" },
  { key: "dp", title: "Double Pana", desc: "2 Same Digits", icon: "DP", color: "#ea580c", border: "#fed7aa", bg: "linear-gradient(135deg,#fffaf5,#fff7ed)", iconBg: "#fff7ed" },
  { key: "tp", title: "Triple Pana", desc: "3 Same Digits", icon: "TP", color: "#dc2626", border: "#fecaca", bg: "linear-gradient(135deg,#fff5f5,#fee2e2)", iconBg: "#fee2e2" },
  { key: "half_sangam", title: "Half Sangam", desc: "Digit + Pana", icon: "HS", color: "#db2777", border: "#fbcfe8", bg: "linear-gradient(135deg,#fff7fb,#fdf2f8)", iconBg: "#fdf2f8" },
  { key: "full_sangam", title: "Full Sangam", desc: "Highest Payout", icon: "FS", color: "#d97706", border: "#facc15", bg: "linear-gradient(135deg,#fffdf2,#fef9c3)", iconBg: "#fef9c3" },
];

function buildHomeRateCards(rateMap = {}) {
  return HOME_RATE_META.map((item) => {
    const value = Number(rateMap?.[item.key] || 0);
    return {
      ...item,
      rateValue: value,
      rate: value > 0 ? `₹${value.toFixed(2)}` : "Updating...",
    };
  });
}

function formatKaRate(value) {
  const rate = Number(value || 0);
  if (!Number.isFinite(rate) || rate <= 0) return "Updating...";
  const payout = rate * 10;
  const formatted = Number.isInteger(payout) ? String(payout) : payout.toFixed(2).replace(/\.?0+$/, "");
  return `10 ka ${formatted}`;
}

const moreGames = [
  { title: "Aviator", subtitle: "Fly High & Win", background: "linear-gradient(135deg,#ef4444,#e11d48)", icon: "AV" },
  { title: "Color Game", subtitle: "Fast Color Picks", background: "linear-gradient(135deg,#14b8a6,#0f766e)", icon: "CL" },
];

const legalPages = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms & Conditions", path: "/terms-and-conditions" },
  { label: "Refund Policy", path: "/refund-policy" },
  { label: "Responsible Gaming", path: "/responsible-gaming" },
  { label: "Contact Us", path: "/contact-us" },
  { label: "About Us", path: "/about-us" },
];

const seoKeywords = [
  { label: "Matka Kings Online Play", note: "Live matka games" },
  { label: "Main Matka Play", note: "Fast access" },
  { label: "Online Matka Result", note: "Live market updates" },
  { label: "Matka Game Rates", note: "Best payout rates" },
  { label: "Aviator Game", note: "Fly high & win" },
  { label: "Color Game", note: "Quick color picks" },
  { label: "Starline Matka", note: "Hourly results" },
  { label: "Jodi Chart Panel Chart", note: "Charts and records" },
];

const page = { minHeight: "100vh", background: "#f8fafc", color: "#0f172a" };
const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.42)", zIndex: 90 };
const desktopSidebar = { position: "fixed", top: "16px", left: "16px", bottom: "16px", width: "286px", background: "#fff", borderRadius: "24px", border: "1px solid #e2e8f0", padding: "14px 12px 18px", boxSizing: "border-box", overflowY: "auto", boxShadow: "18px 0 40px rgba(15,23,42,0.16)", zIndex: 100, transition: "transform 0.3s ease" };
const desktopMainShell = { width: "100%", margin: "0 auto", padding: "14px 18px", boxSizing: "border-box" };
const desktopMainShellLaptop = { padding: "10px 12px" };
const desktopTopbar = { minHeight: "68px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", padding: "10px 16px", boxShadow: "0 12px 30px rgba(15,23,42,0.06)", boxSizing: "border-box", flexWrap: "wrap" };
const desktopTopbarLaptop = { minHeight: "60px", gap: "8px", padding: "8px 10px", borderRadius: "18px", flexWrap: "nowrap" };
const brandButton = { border: 0, background: "transparent", padding: 0, display: "inline-flex", alignItems: "center", cursor: "pointer" };
const desktopNav = { display: "flex", alignItems: "center", gap: "8px", justifyContent: "center", flex: "1 1 420px", minWidth: 0, flexWrap: "wrap" };
const desktopNavLaptop = { gap: "5px", flex: "1 1 auto", flexWrap: "nowrap", overflow: "hidden" };
const topNavButton = { minHeight: "40px", padding: "0 14px", borderRadius: "14px", border: "1px solid #bfdbfe", background: "#fff", color: "#334155", fontWeight: 800, fontSize: "14px", boxShadow: "0 4px 10px rgba(37,99,235,0.06)" };
const topNavButtonActive = { ...topNavButton, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };
const topNavButtonLaptop = { minHeight: "36px", padding: "0 10px", fontSize: "13px", borderRadius: "12px", flexShrink: 1 };
const desktopActions = { display: "flex", alignItems: "center", gap: "8px", flex: "0 1 auto", flexWrap: "wrap", justifyContent: "flex-end" };
const desktopActionsLaptop = { gap: "6px", flexWrap: "nowrap" };
const desktopPlayButton = { minHeight: "40px", padding: "0 16px", borderRadius: "14px", border: 0, background: "linear-gradient(135deg,#34d399,#059669)", color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 10px 22px rgba(5,150,105,0.22)" };
const desktopDownloadButton = { minHeight: "40px", padding: "0 16px", borderRadius: "14px", border: "1px solid #2563eb", background: "#fff", color: "#2563eb", fontWeight: 800, display: "flex", alignItems: "center", gap: "8px" };
const desktopActionButtonLaptop = { minHeight: "36px", padding: "0 11px", fontSize: "13px", borderRadius: "12px" };
const desktopMenuButton = { width: "40px", height: "40px", borderRadius: "14px", border: 0, background: "#f8fafc", color: "#334155", display: "grid", placeItems: "center" };
const desktopMenuButtonLaptop = { width: "36px", height: "36px", borderRadius: "12px" };
const desktopContent = { width: "100%", margin: "14px auto 0" };
const desktopContentLaptop = { marginTop: "10px" };
const mobileDrawer = { position: "fixed", top: 0, left: 0, bottom: 0, width: "280px", maxWidth: "82vw", background: "#fff", zIndex: 100, padding: "10px 12px 18px", boxSizing: "border-box", transition: "transform 0.3s ease", overflowY: "auto", boxShadow: "18px 0 40px rgba(15,23,42,0.16)" };
const drawerTop = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: 10 };
const drawerClose = { width: "36px", height: "36px", borderRadius: "12px", border: 0, background: "#f1f5f9", color: "#334155", display: "grid", placeItems: "center" };
const welcomeCard = { background: "linear-gradient(135deg,#2563eb,#1d4ed8)", borderRadius: "20px", padding: "16px", color: "#fff", display: "grid", gap: "10px", boxShadow: "0 12px 24px rgba(37,99,235,0.24)" };
const welcomeTitle = { fontSize: "18px", fontWeight: 900 };
const welcomeSub = { fontSize: "13px", opacity: 0.9 };
const welcomeButton = { height: "44px", borderRadius: "16px", border: 0, background: "linear-gradient(135deg,#34d399,#059669)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontWeight: 800, boxShadow: "0 10px 20px rgba(5,150,105,0.20)" };
const drawerNav = { display: "grid", gap: "10px", marginTop: "18px" };
const drawerItem = { minHeight: "56px", borderRadius: "18px", border: "1px solid #e5e7eb", background: "#fff", display: "grid", gridTemplateColumns: "44px 1fr 18px", alignItems: "center", gap: "10px", padding: "0 14px", textAlign: "left" };
const drawerItemActive = { background: "#2563eb", borderColor: "#2563eb", color: "#fff", boxShadow: "0 10px 20px rgba(37,99,235,0.20)" };
const drawerItemHighlight = { background: "#22c55e", borderColor: "#22c55e", color: "#fff" };
const drawerIcon = { width: "36px", height: "36px", borderRadius: "12px", display: "grid", placeItems: "center", background: "#f8fafc", color: "#475569" };
const drawerIconActive = { background: "rgba(255,255,255,0.16)", color: "#fff" };
const drawerIconHighlight = { background: "rgba(255,255,255,0.16)", color: "#fff" };
const drawerLabel = { fontSize: "16px", fontWeight: 800 };
const mobileTopbar = { position: "sticky", top: 0, zIndex: 50, height: "78px", background: "rgba(255,255,255,0.96)", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", backdropFilter: "blur(10px)" };
const mobileMenuButton = { width: "42px", height: "42px", borderRadius: "14px", border: 0, background: "#f8fafc", color: "#334155", display: "grid", placeItems: "center" };
const heroWrap = { width: "min(100% - 12px, 430px)", margin: "10px auto 0", display: "grid", gap: "10px" };
const heroWrapDesktop = { width: "100%", maxWidth: "unset", margin: 0 };
const homeTickerWrap = { width: "100%", minHeight: "34px", overflow: "hidden", borderRadius: "18px", border: "1px solid rgba(251,191,36,.22)", background: "linear-gradient(135deg,rgba(255,250,236,.82),rgba(255,255,255,.92))", boxShadow: "inset 0 1px 0 rgba(255,255,255,.82)" };
const homeTickerTrack = { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap", willChange: "transform", gap: "0" };
const homeTickerText = { display: "inline-flex", flexShrink: 0, alignItems: "center", minHeight: "34px", padding: "0 24px", color: "#b45309", fontSize: "12px", fontWeight: 900, letterSpacing: "0.02em", pointerEvents: "none" };
const heroCard = { width: "100%", border: "1px solid rgba(245,158,11,.38)", padding: "8px", borderRadius: "30px", overflow: "hidden", background: "linear-gradient(180deg, rgba(255,250,236,.98) 0%, rgba(255,244,214,.88) 100%)", boxShadow: "0 18px 36px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,.86)", display: "grid", gap: "8px" };
const heroCardDesktop = { borderRadius: "34px", boxShadow: "0 20px 46px rgba(15,23,42,0.12), 0 0 0 1px rgba(255,255,255,.75) inset" };
const heroCardLaptop = { borderRadius: "24px" };
const heroImage = { width: "100%", height: "clamp(178px, 45vw, 240px)", objectFit: "contain", display: "block", borderRadius: "22px", background: "linear-gradient(135deg,#fffaf0,#ffffff)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.92)" };
const heroImageDesktop = { height: "clamp(400px, 34vw, 620px)", minHeight: "400px", maxHeight: "620px", borderRadius: "28px" };
const heroImageLaptop = { height: "clamp(255px, 28vw, 380px)", minHeight: "255px", maxHeight: "380px", borderRadius: "24px" };
const heroFallback = { aspectRatio: "16 / 8", maxHeight: "216px", background: "linear-gradient(135deg,#f8fafc,#eef2ff)", color: "#fff", display: "grid", placeItems: "center", gap: "12px", textAlign: "center", padding: "20px" };
const heroFallbackDesktop = { height: "clamp(390px, 42vw, 680px)", minHeight: "390px", maxHeight: "680px" };
const heroFallbackLaptop = { height: "clamp(280px, 34vw, 430px)", minHeight: "280px", maxHeight: "430px" };
const heroText = { margin: 0, fontSize: "18px", fontWeight: 900, textTransform: "uppercase" };
const downloadStrip = { width: "100%", marginTop: "14px", minHeight: "68px", borderRadius: "20px", border: "1px solid rgba(134,239,172,.95)", background: "linear-gradient(135deg,#2cff8d 0%,#16d667 52%,#0ea54b 100%)", color: "#031b0d", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 16px", boxShadow: "0 0 0 1px rgba(187,247,208,.32) inset, 0 18px 36px rgba(16,185,129,.30), 0 0 24px rgba(34,255,136,.22)" };
const downloadCenter = { display: "flex", alignItems: "center", gap: "12px" };
const downloadIcon = { width: "42px", height: "42px", borderRadius: "14px", background: "rgba(255,255,255,.94)", display: "grid", placeItems: "center", flexShrink: 0, boxShadow: "0 10px 20px rgba(5,150,105,.20)" };
const downloadIconImg = { width: "24px", height: "24px", display: "block" };
const downloadSingleText = { fontSize: "16px", fontWeight: 900, letterSpacing: "0.01em", color: "#031b0d", whiteSpace: "nowrap" };
const colorDownloadIconWrap = { width: "38px", height: "38px", borderRadius: "12px", background: "rgba(255,255,255,.94)", display: "grid", placeItems: "center", flexShrink: 0, boxShadow: "0 8px 18px rgba(5,150,105,.18)" };
const colorDownloadButton = { width: "100%", minHeight: "62px", marginBottom: "20px", border: "1px solid rgba(134,239,172,.95)", borderRadius: "18px", background: "linear-gradient(135deg,#2cff8d 0%,#16d667 52%,#0ea54b 100%)", color: "#031b0d", display: "grid", placeItems: "center", padding: "12px 16px", boxShadow: "0 0 0 1px rgba(187,247,208,.32) inset, 0 14px 28px rgba(16,185,129,.28), 0 0 20px rgba(34,255,136,.18)" };
const content = { width: "min(100% - 24px, 540px)", margin: "24px auto 0", paddingBottom: "110px" };
const contentDesktop = { width: "min(100%, 960px)", margin: "24px auto 0", paddingBottom: "40px" };
const contentLaptop = { width: "min(100%, 1120px)", marginTop: "18px" };
const sectionTitleWrap = { display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "14px" };
const sectionAccent = { width: "4px", height: "40px", borderRadius: "999px", background: "#2563eb", flexShrink: 0 };
const sectionTitle = { margin: 0, fontSize: "18px", fontWeight: 900, color: "#0f172a" };
const sectionSubtitle = { margin: "4px 0 0", color: "#64748b", fontSize: "14px" };
const rateList = { display: "grid", gap: "12px", marginBottom: "24px" };
const rateListDesktop = { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" };
const rateListLaptop = { gap: "12px" };
const rateCard = { width: "100%", minHeight: "74px", display: "grid", gridTemplateColumns: "52px 1fr auto", alignItems: "center", gap: "10px", border: "1px solid", borderRadius: "18px", padding: "0 12px", textAlign: "left", boxShadow: "0 8px 20px rgba(15,23,42,0.04)" };
const rateCardDesktop = { minHeight: "94px", padding: "0 18px" };
const rateCardLaptop = { minHeight: "82px", padding: "0 14px" };
const fullWidthRateCard = { gridColumn: "1 / -1" };
const rateIcon = { width: "40px", height: "40px", borderRadius: "14px", display: "grid", placeItems: "center", fontWeight: 900, fontSize: "12px" };
const rateBody = { display: "grid", gap: "4px" };
const rateTitle = { color: "#0f172a", fontSize: "15px" };
const rateDesc = { color: "#64748b", fontSize: "12px" };
const rateValue = { display: "grid", justifyItems: "end", gap: "2px" };
const rateAmount = { fontSize: "18px", fontWeight: 900 };
const rateUnit = { color: "#334155", fontSize: "12px" };
const liveHeaderRow = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginTop: "8px" };
const refreshButton = { minHeight: "44px", padding: "0 16px", borderRadius: "16px", border: "1px solid #dbeafe", background: "#eff6ff", color: "#2563eb", fontWeight: 800, display: "flex", alignItems: "center", gap: "8px" };
const marketList = { display: "grid", gap: "12px", marginBottom: "18px" };
const marketListDesktop = { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" };
const marketListLaptop = { gap: "12px" };
const marketCard = { width: "100%", minHeight: "112px", borderRadius: "18px", border: "1px solid #e5e7eb", borderBottom: "3px solid #2563eb", background: "#fff", display: "grid", gridTemplateColumns: "1fr 54px", gap: "10px", alignItems: "center", padding: "0 12px", textAlign: "left", boxShadow: "0 8px 18px rgba(15,23,42,0.05)", position: "relative", overflow: "hidden", cursor: "pointer", boxSizing: "border-box" };
const marketGlossSweep = { position: "absolute", top: "-24%", bottom: "-24%", left: "-28%", width: "30%", transform: "skewX(-18deg)", background: "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.72), rgba(255,255,255,0))", opacity: 0, mixBlendMode: "screen", pointerEvents: "none", animation: "mkMarketGlossSweep 4.8s ease-in-out infinite", zIndex: 1 };
const marketCardDesktop = { minHeight: "128px", padding: "0 18px" };
const marketCardLaptop = { minHeight: "112px", padding: "0 14px" };
const marketBody = { display: "grid", gap: "7px", minWidth: 0 };
const marketName = { fontSize: "17px", fontWeight: 900, color: "#0f172a" };
const marketResult = { fontSize: "clamp(15px, 4vw, 18px)", fontWeight: 900, color: "#0f172a", letterSpacing: "0.045em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const marketLinks = { display: "inline-flex", alignItems: "center", gap: "5px", color: "#2563eb", fontWeight: 700, fontSize: "12px", position: "relative", zIndex: 2 };
const marketChartLink = { border: 0, background: "transparent", padding: 0, color: "#2563eb", fontWeight: 800, fontSize: "12px", cursor: "pointer" };
const marketLinkDivider = { color: "#94a3b8", fontWeight: 800 };
const playSquare = { width: "46px", height: "46px", borderRadius: "14px", background: "linear-gradient(180deg,#4ade80,#16a34a)", display: "grid", placeItems: "center", color: "#fff", boxShadow: "0 10px 20px rgba(22,163,74,0.24)" };
const moreGamesList = { display: "grid", gap: "14px", marginBottom: "22px" };
const moreGamesListDesktop = { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" };
const moreGameCard = { width: "100%", minHeight: "92px", border: 0, borderRadius: "18px", color: "#fff", display: "grid", gridTemplateColumns: "60px 1fr 22px", alignItems: "center", gap: "12px", padding: "0 16px", textAlign: "left", boxShadow: "0 10px 24px rgba(15,23,42,0.10)" };
const moreGameIconWrap = { width: "44px", height: "44px", borderRadius: "14px", background: "rgba(255,255,255,0.16)", display: "grid", placeItems: "center", fontWeight: 900, fontSize: "14px", backdropFilter: "blur(6px)" };
const moreGameBody = { display: "grid", gap: "5px" };
const moreGameTitle = { fontSize: "16px", fontWeight: 900 };
const moreGameSubtitle = { fontSize: "13px", opacity: 0.92 };
const moreGameArrow = { fontSize: "28px", fontWeight: 700, opacity: 0.92 };
const playOnlineButton = { width: "100%", height: "56px", borderRadius: "18px", border: 0, background: "linear-gradient(135deg,#34d399,#059669)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontWeight: 900, fontSize: "16px", boxShadow: "0 14px 28px rgba(5,150,105,0.24)" };
const seoCard = { marginTop: "22px", background: "#fff", border: "1px solid #dbeafe", borderRadius: "24px", padding: "18px", boxShadow: "0 10px 24px rgba(37,99,235,0.06)" };
const keywordGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" };
const keywordChip = { minHeight: "58px", border: "1px solid #e2e8f0", borderRadius: "16px", background: "linear-gradient(180deg,#ffffff,#f8fafc)", color: "#0f172a", display: "grid", alignContent: "center", justifyItems: "start", gap: "4px", padding: "0 14px", textAlign: "left", fontWeight: 900 };
const licenseCard = { marginTop: "22px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "24px", padding: "24px 18px", textAlign: "center", boxShadow: "0 10px 24px rgba(15,23,42,0.05)" };
const licenseTitle = { margin: 0, color: "#2563eb", fontSize: "18px", fontWeight: 900 };
const licenseSubtitle = { margin: "8px 0 0", color: "#334155", fontSize: "14px" };
const licenseLogoRow = { marginTop: "18px", display: "flex", alignItems: "center", justifyContent: "center", gap: "18px", flexWrap: "wrap" };
const licenseLogo = { color: "#1e293b", fontSize: "20px", fontWeight: 900 };
const footerCard = { marginTop: "18px", background: "linear-gradient(180deg,#2563eb,#1d4ed8)", color: "#fff", borderRadius: "0 0 24px 24px", padding: "28px 20px 24px", textAlign: "center", boxShadow: "0 18px 36px rgba(37,99,235,0.18)" };
const footerLead = { margin: "18px 0 0", fontSize: "16px", fontWeight: 800, lineHeight: 1.6 };
const footerAddress = { margin: "14px 0 0", fontSize: "15px", opacity: 0.96 };
const footerText = { margin: "16px auto 0", maxWidth: "760px", fontSize: "14px", lineHeight: 1.8, opacity: 0.96 };
const legalLinks = { margin: "18px auto 0", maxWidth: "760px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px 14px", flexWrap: "wrap" };
const legalLink = { border: "1px solid rgba(255,255,255,.28)", borderRadius: "999px", background: "rgba(255,255,255,.12)", color: "#fff", padding: "8px 12px", fontSize: "12px", fontWeight: 800 };
const footerDivider = { width: "100%", height: "1px", background: "rgba(255,255,255,0.25)", marginTop: "20px" };
const footerCopyright = { margin: "14px 0 0", fontSize: "13px", opacity: 0.92 };
const stickyDownloadButton = { position: "fixed", left: "12px", right: "12px", bottom: "12px", zIndex: 70, minHeight: "64px", borderRadius: "18px", border: "1px solid rgba(134,239,172,.95)", background: "linear-gradient(135deg,#2cff8d 0%,#16d667 52%,#0ea54b 100%)", color: "#031b0d", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 14px", boxShadow: "0 0 0 1px rgba(187,247,208,.32) inset, 0 18px 40px rgba(16,185,129,.32), 0 0 24px rgba(34,255,136,.24)" };
const spinIcon = { opacity: 0.75 };

export default Home;
